import { AuditReport } from './digital-audit-constants';
import { DomainConfig } from './domain-config-constants';
import { buildJourney } from './engine/journey-mapper-utils';
import { EditableMilestone, JourneyModel } from './engine/journey-mapper-constants';

const MAX_SUMMARY_PARTS = 3;

/** The journey's current milestones (template + edits) as an editable list. */
export const journeyToEditableMilestones = (journey: JourneyModel): EditableMilestone[] =>
  journey.stages.flatMap(stage =>
    stage.milestones.map(m => ({
      stage: m.stage,
      name: m.name,
      signal: m.signal,
      purpose: m.purpose,
      notes: m.notes
    }))
  );

const keyOf = ({ stage, name }: EditableMilestone): string => `${stage}|${name}`;

const isModified = (a: EditableMilestone, b: EditableMilestone): boolean =>
  a.signal !== b.signal || a.purpose !== b.purpose || a.notes !== b.notes;

/**
 * One short, human-readable line for the change log describing how a
 * milestone list changed, e.g. "added 'Retargeting Email' to stage 3".
 * Milestones are matched by stage + name, so a rename reads as a removal plus
 * an addition. `next === null` means a reset to the template.
 */
export const summarizeMilestoneChange = (
  previous: EditableMilestone[],
  next: EditableMilestone[] | null
): string => {
  if (next === null) return 'reset milestones to the template';

  const previousByKey = new Map(previous.map(m => [keyOf(m), m]));
  const nextByKey = new Map(next.map(m => [keyOf(m), m]));

  const parts = [
    ...next
      .filter(m => !previousByKey.has(keyOf(m)))
      .map(m => `added '${m.name}' to stage ${m.stage}`),
    ...previous
      .filter(m => !nextByKey.has(keyOf(m)))
      .map(m => `removed '${m.name}' from stage ${m.stage}`),
    ...next
      .filter(m => {
        const before = previousByKey.get(keyOf(m));
        return before !== undefined && isModified(before, m);
      })
      .map(m => `edited '${m.name}' in stage ${m.stage}`)
  ];

  if (parts.length === 0) return 'reordered milestones';
  const shown = parts.slice(0, MAX_SUMMARY_PARTS).join('; ');
  const extra = parts.length - MAX_SUMMARY_PARTS;
  return extra > 0 ? `${shown}; +${extra} more` : shown;
};

/**
 * Re-aligns a report to the domain's canonical config (client type, vertical,
 * milestone edits). Used when hydrating a saved run, whose own copy of those
 * values may be older than the canonical ones.
 */
export const applyDomainConfigToReport = (
  report: AuditReport,
  { client_type: clientType, vertical, edited_milestones: milestones }: DomainConfig
): AuditReport => ({
  ...report,
  input: { ...report.input, clientType, vertical },
  journey: buildJourney(
    report.scanResult,
    clientType,
    vertical,
    milestones ?? undefined,
    report.input.clientName
  ),
  editedMilestones: milestones
});
